import type { ReactNode, Ref } from 'react';
import type { ButtonProps } from '@mui/material';
import { Button, CircularProgress } from '@mui/material';

export type SubmitButtonProps = Omit<ButtonProps, 'ref'> & {
  /** When true, shows a spinner, swaps the label, and disables interaction. */
  pending?: boolean;
  /** Optional label while pending (e.g. "Saving…"); keeps `children` if omitted. */
  pendingLabel?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
};

/**
 * Primary action button with a built-in pending state. Defaults to
 * `type="submit"` and `variant="contained"` so it works as an Actions-API /
 * RHF form submit out of the box. All other `Button` props pass through.
 */
export function SubmitButton({
  pending = false,
  pendingLabel,
  children,
  disabled,
  startIcon,
  variant = 'contained',
  type = 'submit',
  ref,
  ...rest
}: SubmitButtonProps) {
  return (
    <Button
      ref={ref}
      type={type}
      variant={variant}
      disabled={pending || disabled}
      startIcon={pending ? <CircularProgress size={16} color="inherit" /> : startIcon}
      {...rest}
    >
      {pending && pendingLabel ? pendingLabel : children}
    </Button>
  );
}
